import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Industries", path: "/industries" },
  { name: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? "bg-background/90 backdrop-blur-md border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="container-custom">
          <nav className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
              <span className="font-heading font-bold text-lg md:text-xl text-text-primary tracking-tight">
                AUSTIN MEDIA BLASTING
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-mono uppercase tracking-widest transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-accent"
                      : "text-text-secondary hover:text-text-primary"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/quote"
                data-testid="nav-quote-cta"
                className="btn-primary flex items-center gap-2"
              >
                Get Quote
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-text-primary"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background pt-24 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-custom flex flex-col gap-6 py-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`block text-3xl font-heading font-bold ${
                      location.pathname === link.path ? "text-accent" : "text-text-primary"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/quote"
                  data-testid="mobile-nav-quote-cta"
                  className="inline-flex items-center gap-2 btn-primary mt-4"
                >
                  Get Quote
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-surface border-t border-white/5">
      <div className="container-custom py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-4">
            <h3 className="font-heading font-bold text-2xl text-text-primary mb-4">
              AUSTIN MEDIA BLASTING
            </h3>
            <p className="text-text-secondary text-sm leading-relaxed max-w-sm">
              Premium surface restoration and specialty blasting services for residential and commercial clients in Austin.
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="label-accent mb-4">Navigate</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-text-secondary hover:text-text-primary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h4 className="label-accent mb-4">Services</h4>
            <ul className="space-y-3 text-sm text-text-secondary">
              <li>Pool Tile Calcium Removal</li>
              <li>Stone Restoration</li>
              <li>Metal Blasting</li>
              <li>Commercial Blasting</li>
              <li>Custom Stencil Etching</li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="label-accent mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-text-secondary">
              <li>Austin, Texas</li>
              <li>
                <a href="mailto:nelsonpoolsatx@gmail.com" className="hover:text-text-primary transition-colors">
                  nelsonpoolsatx@gmail.com
                </a>
              </li>
              <li>
                <a href="tel:+15129888629" className="hover:text-text-primary transition-colors">
                  (512) 988-8629
                </a>
              </li>
            </ul>
            <Link
              to="/quote"
              data-testid="footer-quote-cta"
              className="inline-flex items-center gap-2 btn-primary mt-6"
            >
              Request Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-text-secondary text-xs">
            &copy; {new Date().getFullYear()} Austin Media Blasting. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-text-secondary hover:text-text-primary text-xs transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-text-secondary hover:text-text-primary text-xs transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default Layout;
